import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  getDashboard,
  getDrivers,
  getDriverStats,
  getDriverStoreOrders,
  getDriverStoreStats,
} from "../../api/deliveryCompanyService";
import { formatCurrency, COUPON_DISCOUNT_TYPE_LABELS } from "../Admin/constants";

function couponLabel(t, code, discountType) {
  if (!code) return "-";
  const typeLabel = COUPON_DISCOUNT_TYPE_LABELS[discountType] && t(`dcShared:couponType.${discountType}`);
  return typeLabel ? `${code} (${typeLabel})` : code;
}

export default function Drivers() {
  const { t } = useTranslation("dcDrivers");
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    getDashboard()
      .then(setDashboard)
      .catch(() => {});
  }, []);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {dashboard?.companyName || t("title")}
      </Typography>

      <DriversTab />
    </Box>
  );
}

function DriversTab() {
  const { t } = useTranslation(["dcDrivers", "dcShared"]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    setLoading(true);
    getDrivers({})
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("dcDrivers:loadError"))
      .finally(() => setLoading(false));
  }, []);

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: t("dcShared:name"), flex: 1, minWidth: 160 },
      { field: "phone", headerName: t("dcShared:phone"), width: 140, valueGetter: (v) => v || "-" },
      { field: "vehicleType", headerName: t("dcDrivers:vehicle"), width: 130, valueGetter: (v) => v || "-" },
      { field: "licensePlate", headerName: t("dcDrivers:plate"), width: 110, valueGetter: (v) => v || "-" },
      {
        field: "isAvailable",
        headerName: t("dcShared:available"),
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? t("dcShared:available") : t("dcShared:offline")}
            color={params.value ? "success" : "default"}
          />
        ),
      },
      { field: "rating", headerName: t("dcDrivers:rating"), width: 100 },
      { field: "activeDeliveryCount", headerName: t("dcDrivers:active"), width: 100 },
      { field: "completedDeliveryCount", headerName: t("dcDrivers:completed"), width: 110 },
    ],
    [t]
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedDriver(params.row)}
          sx={{ border: "none", "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      {selectedDriver && <DriverStatsPanel driver={selectedDriver} />}
    </Box>
  );
}

const DRIVER_STATS_PERIODS = ["today", "yesterday", "currentMonth", "pastMonth", "custom"];

function DriverStatsPanel({ driver }) {
  const { t } = useTranslation(["dcDrivers", "dcShared", "boShared"]);
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [storeRows, setStoreRows] = useState([]);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState("");
  const [ordersModalStore, setOrdersModalStore] = useState(null);

  const rangeReady = period !== "custom" || (customStart && customEnd);
  const queryParams = useMemo(
    () => ({
      period,
      startDate: period === "custom" ? customStart : undefined,
      endDate: period === "custom" ? customEnd : undefined,
    }),
    [period, customStart, customEnd]
  );

  useEffect(() => {
    if (!rangeReady) return;

    setLoading(true);
    getDriverStats(driver.userId, queryParams)
      .then((data) => {
        setStats(data);
        setError("");
      })
      .catch(() => setError("dcDrivers:statsLoadError"))
      .finally(() => setLoading(false));
  }, [driver.userId, queryParams, rangeReady]);

  useEffect(() => {
    if (!rangeReady) return;

    setStoreLoading(true);
    getDriverStoreStats(driver.userId, queryParams)
      .then((data) => {
        setStoreRows(data);
        setStoreError("");
      })
      .catch(() => setStoreError("dcDrivers:storeLoadError"))
      .finally(() => setStoreLoading(false));
  }, [driver.userId, queryParams, rangeReady]);

  const storeColumns = useMemo(
    () => [
      { field: "storeName", headerName: t("dcShared:store"), flex: 1, minWidth: 160, sortable: false },
      { field: "ownerName", headerName: t("dcDrivers:businessOwner"), flex: 1, minWidth: 160, sortable: false },
      { field: "orderCount", headerName: t("dcShared:orders"), width: 90, sortable: false },
      { field: "productRevenue", headerName: t("dcDrivers:productsRevenue"), width: 150, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "storeCommission", headerName: t("dcDrivers:platformFeeStore"), width: 160, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "discountAmount",
        headerName: t("dcShared:couponDiscount"),
        width: 150,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "freeDeliveryOrderCount",
        headerName: t("dcDrivers:freeDeliveryOrders"),
        width: 160,
        sortable: false,
      },
      { field: "storeNetRevenue", headerName: t("dcShared:netToStore"), width: 140, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryRevenue", headerName: t("dcDrivers:deliveryRevenue"), width: 150, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryCommission", headerName: t("dcDrivers:platformFeeDelivery"), width: 170, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryNetRevenue", headerName: t("dcDrivers:netFromDelivery"), width: 150, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.isTotalRow ? null : (
            <Button size="small" onClick={() => setOrdersModalStore(params.row)}>
              {t("dcShared:viewOrders")}
            </Button>
          ),
      },
    ],
    [t]
  );

  const storeRowsWithTotal = useMemo(() => {
    if (storeRows.length === 0) return [];

    const totals = storeRows.reduce(
      (acc, s) => ({
        orderCount: acc.orderCount + (s.orderCount || 0),
        productRevenue: acc.productRevenue + (s.productRevenue || 0),
        storeCommission: acc.storeCommission + (s.storeCommission || 0),
        discountAmount: acc.discountAmount + (s.discountAmount || 0),
        freeDeliveryOrderCount: acc.freeDeliveryOrderCount + (s.freeDeliveryOrderCount || 0),
        storeNetRevenue: acc.storeNetRevenue + (s.storeNetRevenue || 0),
        deliveryRevenue: acc.deliveryRevenue + (s.deliveryRevenue || 0),
        deliveryCommission: acc.deliveryCommission + (s.deliveryCommission || 0),
        deliveryNetRevenue: acc.deliveryNetRevenue + (s.deliveryNetRevenue || 0),
      }),
      {
        orderCount: 0,
        productRevenue: 0,
        storeCommission: 0,
        discountAmount: 0,
        freeDeliveryOrderCount: 0,
        storeNetRevenue: 0,
        deliveryRevenue: 0,
        deliveryCommission: 0,
        deliveryNetRevenue: 0,
      }
    );

    return [
      ...storeRows,
      {
        storeId: "__total__",
        storeName: t("dcShared:total"),
        ownerName: "",
        isTotalRow: true,
        ...totals,
      },
    ];
  }, [storeRows, t]);

  return (
    <Box sx={{ mt: 3, p: 2, bgcolor: "background.paper", borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {driver.fullName}
      </Typography>

      <ToggleButtonGroup
        value={period}
        exclusive
        onChange={(e, value) => value && setPeriod(value)}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        {DRIVER_STATS_PERIODS.map((value) => (
          <ToggleButton key={value} value={value}>
            {t(`dcShared:periods.${value}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {period === "custom" && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            type="date"
            label={t("dcShared:from")}
            size="small"
            InputLabelProps={{ shrink: true }}
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
          />
          <TextField
            type="date"
            label={t("dcShared:to")}
            size="small"
            InputLabelProps={{ shrink: true }}
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
          />
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}

      {!rangeReady ? (
        <Typography variant="body2" color="text.secondary">
          {t("dcDrivers:chooseRange")}
        </Typography>
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ flexWrap: "wrap" }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("dcDrivers:totalCollected")}
              </Typography>
              <Typography variant="h4">
                {loading
                  ? "-"
                  : formatCurrency((stats?.storeCollectionsTotal ?? 0) + (stats?.deliveryRevenueTotal ?? 0))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("dcDrivers:totalCollectedHelp", { orders: t("boShared:orderCount", { count: stats?.orderCount ?? 0 }) })}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("dcDrivers:collectedFromStores")}
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.storeCollectionsTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("dcDrivers:collectedFromStoresHelp", { orders: t("boShared:orderCount", { count: stats?.orderCount ?? 0 }) })}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("dcDrivers:revenueFromOrders")}
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.deliveryRevenueTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("dcDrivers:revenueFromOrdersHelp", { orders: t("boShared:orderCount", { count: stats?.orderCount ?? 0 }) })}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("dcDrivers:storeCommission")}
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.storeCommissionTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("dcDrivers:storeCommissionHelp")}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("dcDrivers:deliveryCommission")}
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.deliveryCommissionTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("dcDrivers:deliveryCommissionHelp")}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("dcDrivers:discountsGiven")}
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.discountTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("dcDrivers:discountsGivenHelp")}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("dcDrivers:netForDelivery")}
              </Typography>
              <Typography variant="h4">
                {loading
                  ? "-"
                  : formatCurrency((stats?.deliveryRevenueTotal ?? 0) - (stats?.deliveryCommissionTotal ?? 0))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("dcDrivers:netForDeliveryHelp")}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {rangeReady && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t("dcDrivers:byStore")}
          </Typography>
          {storeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(storeError)}
            </Alert>
          )}
          <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
            <DataGrid
              autoHeight
              rows={storeRowsWithTotal}
              getRowId={(row) => row.storeId}
              columns={storeColumns}
              loading={storeLoading}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                if (!params.row.isTotalRow) setOrdersModalStore(params.row);
              }}
              getRowClassName={(params) => (params.row.isTotalRow ? "total-row" : "")}
              sx={{
                border: "none",
                "& .MuiDataGrid-row": { cursor: "pointer" },
                "& .total-row": {
                  bgcolor: "action.hover",
                  fontWeight: 700,
                  cursor: "default",
                  "& .MuiDataGrid-cell": { fontWeight: 700 },
                },
              }}
            />
          </Box>
        </Box>
      )}

      <DriverStoreOrdersDialog
        driverId={driver.userId}
        store={ordersModalStore}
        queryParams={queryParams}
        onClose={() => setOrdersModalStore(null)}
      />
    </Box>
  );
}

function DriverStoreOrdersDialog({ driverId, store, queryParams, onClose }) {
  const { t } = useTranslation(["dcDrivers", "dcShared"]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!store) return;

    setLoading(true);
    getDriverStoreOrders(driverId, store.storeId, queryParams)
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("dcDrivers:ordersLoadError"))
      .finally(() => setLoading(false));
  }, [driverId, store, queryParams]);

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: t("dcShared:orderNumber"), width: 130 },
      { field: "customerName", headerName: t("dcShared:customer"), flex: 1, minWidth: 140 },
      {
        field: "deliveredAt",
        headerName: t("dcDrivers:deliveredAt"),
        width: 170,
        valueFormatter: (v) => (v ? new Date(v).toLocaleString() : "-"),
      },
      { field: "productRevenue", headerName: t("dcDrivers:products"), width: 110, valueFormatter: (v) => formatCurrency(v) },
      { field: "storeCommission", headerName: t("dcDrivers:storeFee"), width: 110, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "couponCode",
        headerName: t("dcDrivers:coupon"),
        width: 170,
        renderCell: (params) =>
          params.row.couponCode ? (
            <Chip
              size="small"
              label={couponLabel(t, params.row.couponCode, params.row.couponDiscountType)}
              color={[3, 4].includes(params.row.couponDiscountType) ? "info" : "default"}
            />
          ) : (
            "-"
          ),
      },
      { field: "discountAmount", headerName: t("dcShared:couponDiscount"), width: 140, valueFormatter: (v) => formatCurrency(v) },
      { field: "storeNetRevenue", headerName: t("dcShared:netToStore"), width: 130, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryRevenue", headerName: t("dcDrivers:delivery"), width: 110, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryCommission", headerName: t("dcShared:deliveryFee"), width: 120, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryNetRevenue", headerName: t("dcDrivers:netDelivery"), width: 130, valueFormatter: (v) => formatCurrency(v) },
    ],
    [t]
  );

  return (
    <Dialog open={Boolean(store)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t("dcDrivers:ordersDialogTitle", { store: store?.storeName })}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(error)}
          </Alert>
        )}
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.orderId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </DialogContent>
    </Dialog>
  );
}

